import { BlogPost, BlogCategory, BlogAuthor } from "@/types/blog";
import { apiService } from "./api";

// API Endpoints
const ENDPOINTS = {
  posts: "/blogs",
  post: (slug: string) => `/blogs/${slug}`,
  categories: "/blog-categories",
  authors: "/blog/authors",
  featured: "/blogs/featured",
  related: (postId: string) => `/blogs/${postId}/related`,
  search: "/blog/search",
} as const;

// Helper function to compute full image URL
const computeImageUrl = (image: string): string => {
  if (!image) return '';
  
  // If it's already a full URL (starts with http/https), return as is
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  
  // In development, try to use the cached ngrok URL if available
  let baseUrl = import.meta.env.VITE_API_URL || '';
  if (import.meta.env.DEV && import.meta.env.VITE_USE_NGROK === 'true') {
    const cachedUrl = (window as any).__NGROK_URL_CACHE__;
    if (cachedUrl) {
      baseUrl = cachedUrl;
    }
  }
  
  return `${baseUrl}/storage/${image}`;
};

// Transform API response to include full image URL
const transformBlogPost = (post: any): BlogPost => {
  return {
    ...post,
    image_url: computeImageUrl(post.image || post.coverImage || post.thumbnail),
    publishedAt: post.published_at || post.publishedAt,
    lastUpdated: post.updated_at || post.lastUpdated,
    featured: post.is_featured || post.featured,
  };
};

// Blog Service
export const blogService = {
  // Get all posts with optional filters
  getPosts: async (params?: {
    category?: string;
    tag?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const data = await apiService.get<{
      data: any[];
      total: number;
      current_page: number;
      last_page: number;
      per_page: number;
    }>(ENDPOINTS.posts, { params, isPublic: true });
    return {
      ...data,
      data: data.data.map(transformBlogPost),
    };
  },

  // Get a single post by slug
  getPost: async (slug: string) => {
    const data = await apiService.get<any>(ENDPOINTS.post(slug), { isPublic: true });
    return transformBlogPost(data);
  },

  // Get all categories
  getCategories: async () => {
    const data = await apiService.get<BlogCategory[]>(ENDPOINTS.categories, { isPublic: true });
    return data;
  },

  // Get all authors
  getAuthors: async () => {
    const data = await apiService.get<BlogAuthor[]>(ENDPOINTS.authors, { isPublic: true });
    return data;
  },

  // Get featured posts
  getFeaturedPosts: async (limit = 3) => {
    const data = await apiService.get<any[]>(ENDPOINTS.featured, {
      params: { limit },
      isPublic: true
    });
    return data.map(transformBlogPost);
  },

  // Get related posts
  getRelatedPosts: async (postId: string, limit = 3) => {
    const data = await apiService.get<any[]>(ENDPOINTS.related(postId), {
      params: { limit },
      isPublic: true
    });
    return data.map(transformBlogPost);
  },

  // Search posts
  searchPosts: async (query: string) => {
    const data = await apiService.get<{
      posts: any[];
      total: number;
    }>(ENDPOINTS.search, {
      params: { q: query },
      isPublic: true
    });
    return {
      ...data,
      posts: data.posts.map(transformBlogPost),
    };
  },

  // Increment post views
  incrementViews: async (postId: string) => {
    await apiService.post(`/blog/posts/${postId}/view`, undefined, { isPublic: true });
  },

  // Like a post
  likePost: async (postId: string) => {
    const data = await apiService.post<{ likes: number }>(`/blog/posts/${postId}/like`, undefined, { isPublic: true });
    return data;
  },

  // Create a new post (admin only)
  createPost: async (values: Partial<BlogPost>) => {
    const data = await apiService.post<BlogPost>(ENDPOINTS.posts, values);
    return data;
  },

  // Update an existing post (admin only)
  updatePost: async (id: string, values: Partial<BlogPost>) => {
    const data = await apiService.put<BlogPost>(ENDPOINTS.post(id), values);
    return data;
  },

  // Delete a post (admin only)
  deletePost: async (id: string) => {
    await apiService.delete(ENDPOINTS.post(id));
  },
}; 